import { useState } from "react";
import { Hand, Mic, MicOff, Phone, Video, VideoOff } from "lucide-react";
import DevicePicker from "../../../components/common/DevicePicker";

interface AdvocateControlBarProps {
  handRaised?: boolean;
  handPending?: boolean;
  onToggleHand?: () => void;
  micOn?: boolean;
  micLocked?: boolean;
  camOn?: boolean;
  cameraId?: string;
  microphoneId?: string;
  onToggleMic?: () => void;
  onToggleCam?: () => void;
  onSelectCamera?: (device: MediaDeviceInfo) => void;
  onSelectMicrophone?: (device: MediaDeviceInfo) => void;
  /** Fired only after the user confirms in the leave dialog. */
  onLeave?: () => void;
}

const AdvocateControlBar = ({
  handRaised = false,
  handPending = false,
  onToggleHand,
  micOn = true,
  micLocked = false,
  camOn = true,
  cameraId,
  microphoneId,
  onToggleMic,
  onToggleCam,
  onSelectCamera,
  onSelectMicrophone,
  onLeave,
}: AdvocateControlBarProps) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const micMuted = !micOn || micLocked;

  return (
    <div className="relative z-30 flex shrink-0 justify-center" role="group" aria-label="Hearing controls">
      <div className="flex items-center gap-2 rounded-full bg-slate-800/95 p-2 shadow-xl ring-1 ring-white/10 sm:gap-4">
        {onToggleHand && (
          <button
            type="button"
            onClick={onToggleHand}
            disabled={handPending}
            aria-pressed={handRaised}
            aria-label={handRaised ? "Lower hand" : "Raise hand"}
            className={`flex h-10 items-center gap-2 rounded-full px-4 text-sm font-medium transition disabled:opacity-50 ${handRaised ? "bg-amber-400 text-slate-900 hover:bg-amber-300" : "bg-slate-700 text-white hover:bg-slate-600"}`}
          >
            <Hand className="h-4 w-4" />
          </button>
        )}
        {/* Mic + device picker */}
        <div className="flex items-center">
          <button
            type="button"
            onClick={onToggleMic}
            disabled={micLocked}
            aria-pressed={micOn}
            aria-label={
              micLocked
                ? "Microphone muted by judge"
                : micOn
                  ? "Mute microphone"
                  : "Unmute microphone"
            }
            title={micLocked ? "Muted by judge" : undefined}
            className={`flex h-10 w-12 items-center justify-center rounded-l-full transition ${
              micLocked
                ? "cursor-not-allowed bg-red-700 text-white"
                : micMuted
                  ? "bg-red-600 text-white hover:bg-red-700"
                  : "bg-blue-700 text-white hover:bg-blue-800"
            }`}
          >
            {!micMuted ? (
              <Mic className="h-4 w-4" />
            ) : (
              <MicOff className="h-4 w-4" />
            )}
          </button>
          {onSelectMicrophone && (
            <DevicePicker
              kind="microphone"
              selectedId={microphoneId}
              onSelect={onSelectMicrophone}
              variant="segmented"
              menuPosition="viewport-bottom"
            />
          )}
        </div>

        {/* Camera + device picker */}
        <div className="hidden items-center">
          <button
            type="button"
            onClick={onToggleCam}
            aria-pressed={camOn}
            aria-label={camOn ? "Turn camera off" : "Turn camera on"}
            className={`flex h-10 w-12 items-center justify-center rounded-l-full transition ${
              camOn
                ? "bg-blue-700 text-white hover:bg-blue-800"
                : "bg-red-600 text-white hover:bg-red-700"
            }`}
          >
            {camOn ? (
              <Video className="h-5 w-5" />
            ) : (
              <VideoOff className="h-5 w-5" />
            )}
          </button>
          {onSelectCamera && (
            <DevicePicker
              kind="camera"
              selectedId={cameraId}
              onSelect={onSelectCamera}
              variant="segmented"
            />
          )}
        </div>

        <button
          type="button"
          onClick={() => setConfirmOpen(true)}
          aria-label="Leave"
          className="flex h-10 w-14 items-center justify-center rounded-full bg-red-600 text-white transition hover:bg-red-700"
        >
          <Phone className="h-4 w-4 rotate-[135deg]" />
        </button>
      </div>

      <LeaveMeetingModal
        isOpen={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          onLeave?.();
        }}
      />
    </div>
  );
};

export default AdvocateControlBar;

const LeaveMeetingModal = ({
  isOpen,
  onCancel,
  onConfirm,
}: {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-red-600">
          <Phone className="h-6 w-6 rotate-[135deg]" />
        </div>
        <h2 className="text-center text-lg font-semibold text-gray-900">
          Leave hearing?
        </h2>
        <p className="mt-1 text-center text-sm text-gray-500">
          You will be disconnected from the courtroom. You can rejoin if the
          hearing is still active.
        </p>

        <div className="mt-6 flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-xl border border-gray-300 px-4 py-2.5 font-medium text-gray-700 transition hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex-1 rounded-xl bg-red-600 px-4 py-2.5 font-medium text-white transition hover:bg-red-700"
          >
            Leave
          </button>
        </div>
      </div>
    </div>
  );
};
